import { useContext } from "react";
import { gql, useQuery } from "@apollo/client";
import StarekContext from "../context/StarekContext";

/** Setting row (Settings page, "ui" category) holding the user's list layout. */
export const LIST_LAYOUT_SETTING_CODE = "system.ui.list.page_layout";

/** Used until the setting loads, and when it is missing or holds a bad value. */
export const DEFAULT_LIST_LAYOUT = "5x4";

/** Hard ceiling on records per page, whatever the setting says. */
export const MAX_PAGE_SIZE = 50;

const MAX_COLUMNS = 6;

// Same user_preferences arguments as the Settings page query, so a save there
// updates this cache entry too.
const GET_LIST_LAYOUT = gql`
    query GetListLayoutSetting($code: String!, $userId: Int!) {
        settings(where: { code: { equals: $code } }) {
            id
            meta
            user_preferences(where: { owned_by_id: { equals: $userId } }) {
                value
            }
        }
    }
`;

/**
 * "5x4" -> { columns: 5, rows: 4, pageSize: 20 }. Null for anything malformed or
 * over MAX_PAGE_SIZE, so a bad value falls back instead of paging 500 rows.
 */
export const parseListLayout = (value) => {
    const match = /^\s*(\d+)\s*x\s*(\d+)\s*$/i.exec(String(value ?? ""));
    if (!match) return null;
    const columns = parseInt(match[1], 10);
    const rows = parseInt(match[2], 10);
    if (columns < 1 || columns > MAX_COLUMNS || rows < 1) return null;
    if (columns * rows > MAX_PAGE_SIZE) return null;
    return { columns, rows, pageSize: columns * rows };
};

const FALLBACK = parseListLayout(DEFAULT_LIST_LAYOUT);

/**
 * Card grid classes for `columns` cards per row on wide screens. Narrower screens
 * step down (4 / 3 / 2 / 1 per row) so cards keep a usable width; the page size
 * stays the same, the grid just wraps into more rows.
 */
export const cardGridClass = (columns) => {
    const classes = ["row", "g-3", "row-cols-1", "row-cols-sm-2", "row-cols-md-3"];
    if (columns <= 4) {
        classes.push(`row-cols-lg-${columns}`);
    } else {
        classes.push("row-cols-lg-4", `row-cols-xl-${columns}`);
    }
    return classes.join(" ");
};

/**
 * The signed-in user's list layout from the Settings page: card columns × rows per
 * page. The product of the two is the page size for every paginated list, table
 * and card view alike, capped at MAX_PAGE_SIZE.
 *
 * `ready` turns true once the setting has loaded or failed (a user without access
 * to settings gets the default), so lists can hold their first request until the
 * page size is known instead of fetching twice.
 */
export default function useListLayout() {
    const starekContext = useContext(StarekContext);
    const userId = parseInt(starekContext?.user?.id, 10);

    const { data, loading, error } = useQuery(GET_LIST_LAYOUT, {
        client: starekContext?.engineClient,
        variables: { code: LIST_LAYOUT_SETTING_CODE, userId },
        skip: !userId,
        fetchPolicy: "cache-and-network",
    });

    const setting = data?.settings?.[0];
    const layout =
        parseListLayout(setting?.user_preferences?.[0]?.value) ??
        parseListLayout(setting?.meta?.default) ??
        FALLBACK;

    return {
        ...layout,
        ready: !userId || !!data || !!error || !loading,
    };
}
